import React, { useState } from 'react';
import './TransactionSearch.css';

/**
 * Reusable Transaction Search & Filter Component
 * Used by both Previous.js and PreviousAll.js
 * 
 * IMPORTANT: Search only triggers on explicit confirm (button click or Enter key)
 * — never while typing — to minimize Appwrite read usage.
 * 
 * Props:
 * - onSearch(params)       : called when user confirms search
 * - onClear()              : called when user clears search
 * - isSearching (bool)     : loading state
 * - isSearchMode (bool)    : whether currently showing search results
 * - resultCount (number)   : number of groups found
 * - hasMore (bool)         : whether more results are available
 * - onLoadMore()           : called when user clicks "Load More"
 */
export default function TransactionSearch({
    onSearch,
    onClear,
    isSearching = false,
    isSearchMode = false,
    resultCount = 0,
    hasMore = false,
    onLoadMore,
}) {
    const [searchText, setSearchText] = useState('');
    const [searchField, setSearchField] = useState('name');
    const [showFilters, setShowFilters] = useState(false);

    // Filters
    const [filterType, setFilterType] = useState('');
    const [filterDateFrom, setFilterDateFrom] = useState('');
    const [filterDateTo, setFilterDateTo] = useState('');
    const [filterMinQty, setFilterMinQty] = useState('');

    function handleSearch() {
        const hasText = searchText.trim().length > 0;
        const hasFilters = filterType || filterDateFrom || filterDateTo || filterMinQty;
        if (!hasText && !hasFilters) return;

        onSearch({
            searchText: searchText.trim(),
            searchField,
            type: filterType,
            dateFrom: filterDateFrom,
            dateTo: filterDateTo,
            minQty: filterMinQty,
        });
    }

    function handleKeyDown(e) {
        if (e.key === 'Enter') {
            handleSearch();
        }
    }

    function handleClear() {
        setSearchText('');
        setSearchField('name');
        setFilterType('');
        setFilterDateFrom('');
        setFilterDateTo('');
        setFilterMinQty('');
        setShowFilters(false);
        onClear();
    }

    function clearFilters() {
        setFilterType('');
        setFilterDateFrom('');
        setFilterDateTo('');
        setFilterMinQty('');
    }

    const hasActiveFilters = filterType || filterDateFrom || filterDateTo || filterMinQty;

    return (
        <div className="tx-search-wrapper">
            {/* Search Bar */}
            <div className="tx-search-bar">
                <div className="tx-search-input-group">
                    <span className="tx-search-icon">🔍</span>
                    <input
                        id="tx-search-input"
                        type="text"
                        className="tx-search-input"
                        placeholder="بحث في المعاملات..."
                        value={searchText}
                        onChange={e => setSearchText(e.target.value)}
                        onKeyDown={handleKeyDown}
                        disabled={isSearching}
                    />
                </div>

                <select
                    id="tx-search-field"
                    className="tx-search-field-select"
                    value={searchField}
                    onChange={e => setSearchField(e.target.value)}
                    disabled={isSearching}
                >
                    <option value="name">الاسم</option>
                    <option value="item">الصنف</option>
                    <option value="code">الكود</option>
                    <option value="user">المستخدم</option>
                </select>

                <button
                    id="tx-search-btn"
                    className="tx-search-btn"
                    onClick={handleSearch}
                    disabled={isSearching}
                >
                    {isSearching ? '...' : 'بحث'}
                </button>
            </div>

            {/* Filter Toggle */}
            <div className="tx-filter-toggle-row">
                <button
                    className={`tx-filter-toggle-btn ${hasActiveFilters ? 'has-filters' : ''}`}
                    onClick={() => setShowFilters(!showFilters)}
                >
                    ⚙ الفلاتر {showFilters ? '▲' : '▼'}
                    {hasActiveFilters && <span className="tx-filter-badge">!</span>}
                </button>

                {isSearchMode && (
                    <button className="tx-search-clear-btn" onClick={handleClear}>
                        ✕ مسح البحث
                    </button>
                )}
            </div>

            {/* Collapsible Filter Panel */}
            {showFilters && (
                <div className="tx-filter-panel">
                    <div className="tx-filter-row">
                        <div className="tx-filter-field">
                            <label htmlFor="tx-filter-type">النوع:</label>
                            <select
                                id="tx-filter-type"
                                value={filterType}
                                onChange={e => setFilterType(e.target.value)}
                            >
                                <option value="">الكل</option>
                                <option value="in">وارد</option>
                                <option value="out">منصرف</option>
                                <option value="production">إنتاج</option>
                            </select>
                        </div>

                        <div className="tx-filter-field">
                            <label htmlFor="tx-filter-minqty">الكمية (حد أدنى):</label>
                            <input
                                id="tx-filter-minqty"
                                type="number"
                                min="0"
                                placeholder="مثال: 10"
                                value={filterMinQty}
                                onChange={e => setFilterMinQty(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="tx-filter-row">
                        <div className="tx-filter-field">
                            <label htmlFor="tx-filter-datefrom">من تاريخ:</label>
                            <input
                                id="tx-filter-datefrom"
                                type="date"
                                value={filterDateFrom}
                                onChange={e => setFilterDateFrom(e.target.value)}
                            />
                        </div>

                        <div className="tx-filter-field">
                            <label htmlFor="tx-filter-dateto">إلى تاريخ:</label>
                            <input
                                id="tx-filter-dateto"
                                type="date"
                                value={filterDateTo}
                                onChange={e => setFilterDateTo(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="tx-filter-actions">
                        <button className="tx-filter-clear-btn" onClick={clearFilters}>
                            مسح الفلاتر
                        </button>
                    </div>
                </div>
            )}

            {/* Search Status */}
            {isSearchMode && !isSearching && (
                <div className="tx-search-status">
                    <span>نتائج البحث: <strong>{resultCount}</strong> معاملة</span>
                </div>
            )}
        </div>
    );
}
